import React, { useState, memo } from 'react';
import { MapPin, Ruler, Tag, ChevronRight, AlertTriangle, Pause, Info, Eye, Calendar } from 'lucide-react';
import ModelBuilderBadge from './ModelBuilderBadge';
import { ModelBuilderService } from '../services/modelBuilderService';
import { ProjectService } from '../services/projectService';
import ProjectDetailsModal from './ProjectDetailsModal';
import FullSubitemModal from './FullSubitemModal';
import { useAuth } from '../contexts/AuthContext';
import { useUserRole } from '../hooks/useUserRoleSecure';

const ReadyToModelCard = ({ project, modelBuilders, onAssignmentChange, onUpdateProject }) => {
  const { user } = useAuth();
  const { canAccessOperationsDashboards } = useUserRole();
  const [isUpdating, setIsUpdating] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showSubitemsModal, setShowSubitemsModal] = useState(false);

  // Format job number with revision
  const jobNumberDisplay = project.jobNumber
    ? `${project.jobNumber}${project.rev ? `-${project.rev}` : ''}`
    : 'No Job #';

  // Get eligibility for each model builder
  const getBuilderEligibility = (builderId) => {
    const eligibility = project.eligibility?.find(e => e.employee_id === builderId);
    return {
      isEligible: eligibility?.is_eligible_by_rules || false,
      isManuallyAssigned: eligibility?.is_manually_assigned || false,
      assignedByName: eligibility?.assigned_by?.name || null
    };
  };

  // Handle badge click (toggle assignment)
  const handleBadgeClick = async (builderId) => {
    if (!canAccessOperationsDashboards) return;
    if (isUpdating) return;

    try {
      setIsUpdating(true);

      const eligibility = getBuilderEligibility(builderId);
      const shouldAssign = !eligibility.isManuallyAssigned;

      await ModelBuilderService.toggleBuilderAssignment(
        project.id,
        builderId,
        user.user_metadata?.employee_id,
        shouldAssign
      );

      // Notify parent to refresh data
      if (onAssignmentChange) {
        onAssignmentChange();
      }
    } catch (error) {
      console.error('Error toggling builder assignment:', error);
      alert('Failed to update assignment. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle advance to modeling
  const handleAdvanceToModeling = (e) => {
    e.preventDefault();
    e.stopPropagation();

    onUpdateProject(project.id, 'modelingStatus', 'Modeling');
  };

  // Handle modal refresh
  const handleModalRefresh = () => {
    if (onAssignmentChange) {
      onAssignmentChange();
    }
  };

  // Format template filters for display
  const getRelevantFilters = () => {
    if (!project.templateFilter || project.templateFilter.length === 0) {
      return null;
    }

    // Filter out generic filters and show relevant ones
    const relevant = project.templateFilter
      .filter(f => !f.includes('00-All Projects'))
      .map(f => f.replace(/^\d+-/, '')) // Remove leading numbers
      .slice(0, 3); // Show max 3

    return relevant.length > 0 ? relevant : null;
  };

  const relevantFilters = getRelevantFilters();

  return (
    <div
      style={{
        background: project.isHighPriority
          ? 'rgba(239, 68, 68, 0.1)'
          : project.isSoftHold
            ? 'rgba(107, 114, 128, 0.35)'
            : 'var(--dark-elevated)',
        border: project.isSoftHold
          ? '1px solid rgba(234, 179, 8, 0.5)'
          : project.isHighPriority
            ? '1px solid rgba(239, 68, 68, 0.3)'
            : '1px solid var(--gray-600)',
        borderLeft: project.isSoftHold
          ? '4px solid rgba(234, 179, 8, 0.8)'
          : project.isHighPriority
            ? '4px solid #ef4444'
            : '1px solid var(--gray-600)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--space-4)',
        cursor: 'default',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        opacity: isUpdating ? 0.6 : project.isSoftHold ? 0.7 : 1,
        pointerEvents: isUpdating ? 'none' : 'auto',
      }}
    >
      {/* Card Header */}
      <div
        style={{
          marginBottom: 'var(--space-3)',
          paddingBottom: 'var(--space-3)',
          borderBottom: '1px solid var(--gray-700)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
          <h3
            style={{
              fontSize: 'var(--text-lg)',
              fontWeight: 'var(--font-semibold)',
              color: 'var(--gray-50)',
              margin: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              lineHeight: 1.3,
              flex: 1,
            }}
            title={`${jobNumberDisplay} - ${project.projectName || 'Unnamed Project'}`}
          >
            <span style={{ color: 'var(--cta-blue)' }}>{jobNumberDisplay}</span>
            {' - '}
            {project.projectName || 'Unnamed Project'}
          </h3>
          {project.isSoftHold && (
            <span
              style={{
                padding: '4px 8px',
                backgroundColor: 'rgba(234, 179, 8, 0.9)',
                color: '#000',
                fontSize: 'var(--text-xs)',
                fontWeight: 'var(--font-bold)',
                borderRadius: 'var(--radius-sm)',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                whiteSpace: 'nowrap',
              }}
            >
              Paused
            </span>
          )}
        </div>

        {/* Action Buttons */}
        {canAccessOperationsDashboards && (
          <div
            style={{
              display: 'flex',
              gap: '4px',
              marginBottom: 'var(--space-2)',
            }}
          >
            {/* High Priority Toggle */}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onUpdateProject(project.id, 'isHighPriority', !project.isHighPriority);
              }}
              style={{
                padding: '4px',
                background: 'none',
                border: 'none',
                color: project.isHighPriority ? '#ef4444' : 'var(--gray-400)',
                cursor: 'pointer',
                borderRadius: '4px',
                transition: 'background-color 0.2s, color 0.2s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              title={project.isHighPriority ? 'Remove high priority' : 'Set as high priority'}
            >
              <AlertTriangle
                size={20}
                fill={project.isHighPriority ? '#ef4444' : 'none'}
                color={project.isHighPriority ? 'white' : '#9CA3AF'}
                strokeWidth={project.isHighPriority ? 2.5 : 2}
              />
            </button>

            {/* Soft Hold Toggle */}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onUpdateProject(project.id, 'isSoftHold', !project.isSoftHold);
              }}
              style={{
                padding: '4px',
                background: 'none',
                border: 'none',
                color: project.isSoftHold ? '#eab308' : 'var(--gray-400)',
                cursor: 'pointer',
                borderRadius: '4px',
                transition: 'background-color 0.2s, color 0.2s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              title={project.isSoftHold ? 'Remove from soft hold' : 'Put on soft hold'}
            >
              <Pause
                size={20}
                fill={project.isSoftHold ? '#eab308' : 'none'}
                color={project.isSoftHold ? '#eab308' : '#9CA3AF'}
                strokeWidth={project.isSoftHold ? 2.5 : 2}
              />
            </button>

            {/* Info Button */}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setShowDetailsModal(true);
              }}
              disabled={isUpdating}
              style={{
                padding: '8px',
                background: 'none',
                border: 'none',
                color: 'var(--gray-400)',
                cursor: isUpdating ? 'not-allowed' : 'pointer',
                borderRadius: '8px',
                transition: 'background-color 0.2s, color 0.2s, transform 0.2s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '36px',
                height: '36px',
                minWidth: '36px',
                minHeight: '36px',
                maxWidth: '36px',
                maxHeight: '36px',
                flexShrink: 0,
              }}
              title="View project details"
              onMouseEnter={(e) => {
                if (!isUpdating) {
                  e.currentTarget.style.backgroundColor = 'var(--gray-800)';
                  e.currentTarget.style.color = 'var(--cta-blue)';
                  e.currentTarget.style.transform = 'scale(1.05)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isUpdating) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = 'var(--gray-400)';
                  e.currentTarget.style.transform = 'scale(1)';
                }
              }}
            >
              <Info size={20} />
            </button>

            {/* Subitems Button */}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setShowSubitemsModal(true);
              }}
              disabled={isUpdating}
              style={{
                padding: '8px',
                background: 'none',
                border: 'none',
                color: 'var(--gray-400)',
                cursor: isUpdating ? 'not-allowed' : 'pointer',
                borderRadius: '8px',
                transition: 'background-color 0.2s, color 0.2s, transform 0.2s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '36px',
                height: '36px',
                minWidth: '36px',
                minHeight: '36px',
                maxWidth: '36px',
                maxHeight: '36px',
                flexShrink: 0,
              }}
              title="View subitems"
              onMouseEnter={(e) => {
                if (!isUpdating) {
                  e.currentTarget.style.backgroundColor = 'var(--gray-800)';
                  e.currentTarget.style.color = 'var(--cta-blue)';
                  e.currentTarget.style.transform = 'scale(1.05)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isUpdating) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = 'var(--gray-400)';
                  e.currentTarget.style.transform = 'scale(1)';
                }
              }}
            >
              <Eye size={20} />
            </button>
          </div>
        )}

        {/* Client Name */}
        {project.clientName && (
          <div
            style={{
              fontSize: 'var(--text-sm)',
              color: 'var(--gray-400)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={project.clientName}
          >
            {project.clientName}
          </div>
        )}
      </div>

      {/* Card Body - Project Details */}
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-2)',
          marginBottom: 'var(--space-3)',
        }}
      >
        {/* Location */}
        {project.location && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
            }}
          >
            <MapPin
              size={14}
              style={{
                flexShrink: 0,
                color: 'var(--gray-400)',
              }}
            />
            <span
              style={{
                fontSize: 'var(--text-sm)',
                color: 'var(--gray-300)',
              }}
            >
              {project.location}
            </span>
          </div>
        )}

        {/* Acreage */}
        {project.approximateAcreage && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
            }}
          >
            <Ruler
              size={14}
              style={{
                flexShrink: 0,
                color: 'var(--gray-400)',
              }}
            />
            <span
              style={{
                fontSize: 'var(--text-sm)',
                color: 'var(--gray-300)',
              }}
            >
              {project.approximateAcreage} acres
            </span>
          </div>
        )}

        {/* Due Date */}
        {project.dueDate && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
            }}
          >
            <Calendar
              size={14}
              style={{
                flexShrink: 0,
                color: new Date(project.dueDate) < new Date() ? '#ef4444' : 'var(--gray-400)',
              }}
            />
            <span
              style={{
                fontSize: 'var(--text-sm)',
                color: new Date(project.dueDate) < new Date() ? '#ef4444' : 'var(--gray-300)',
                fontWeight: new Date(project.dueDate) < new Date() ? 'var(--font-medium)' : 'normal',
              }}
            >
              Due {new Date(project.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </span>
          </div>
        )}

        {/* Template Filters */}
        {relevantFilters && (
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 'var(--space-2)',
            }}
          >
            <Tag
              size={14}
              style={{
                flexShrink: 0,
                marginTop: '2px',
                color: 'var(--gray-400)',
              }}
            />
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 'var(--space-1)',
              }}
            >
              {relevantFilters.map((filter, index) => (
                <span
                  key={index}
                  style={{
                    fontSize: 'var(--text-xs)',
                    color: 'var(--gray-400)',
                    backgroundColor: 'var(--dark-surface)',
                    padding: '2px 6px',
                    borderRadius: 'var(--radius-sm)',
                  }}
                >
                  {filter}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Card Footer - Model Builder Badges */}
      <div
        style={{
          paddingTop: 'var(--space-3)',
          borderTop: '1px solid var(--gray-700)',
        }}
      >
        <div
          style={{
            marginBottom: 'var(--space-2)',
            fontSize: 'var(--text-xs)',
            fontWeight: 'var(--font-medium)',
            color: 'var(--gray-400)',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}
        >
          Model Builders
        </div>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 'var(--space-2)',
            marginBottom: 'var(--space-3)',
          }}
        >
          {modelBuilders.map((builder) => {
            const eligibility = getBuilderEligibility(builder.id);
            return (
              <ModelBuilderBadge
                key={builder.id}
                employeeId={builder.id}
                employeeName={builder.name}
                isEligible={eligibility.isEligible}
                isManuallyAssigned={eligibility.isManuallyAssigned}
                onClick={handleBadgeClick}
                userCanAssign={canAccessOperationsDashboards}
                assignedByName={eligibility.assignedByName}
              />
            );
          })}
        </div>

        {/* Advance to Modeling Button */}
        <button
          onClick={handleAdvanceToModeling}
            disabled={isUpdating}
            style={{
              width: '100%',
              padding: 'var(--space-2) var(--space-3)',
              backgroundColor: 'var(--cta-orange)',
              color: 'white',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--text-sm)',
              fontWeight: 'var(--font-medium)',
              cursor: isUpdating ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 'var(--space-2)',
              transition: 'all var(--transition-fast)',
              opacity: isUpdating ? 0.5 : 1,
            }}
            onMouseEnter={(e) => {
              if (!isUpdating) {
                e.currentTarget.style.backgroundColor = '#f97316';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isUpdating) {
                e.currentTarget.style.backgroundColor = 'var(--cta-orange)';
                e.currentTarget.style.transform = 'translateY(0)';
              }
            }}
          >
            <span>Advance to Modeling</span>
            <ChevronRight size={16} />
          </button>
      </div>

      {/* Modals */}
      {showDetailsModal && (
        <ProjectDetailsModal
          project={project}
          isOpen={showDetailsModal}
          onClose={() => setShowDetailsModal(false)}
          onProjectUpdate={handleModalRefresh}
        />
      )}

      {showSubitemsModal && (
        <FullSubitemModal
          project={project}
          isOpen={showSubitemsModal}
          onClose={() => setShowSubitemsModal(false)}
          onUpdateProject={handleModalRefresh}
          employees={[]}
        />
      )}
    </div>
  );
};

export default memo(ReadyToModelCard);
